import type { IAllProps as EditorProps } from '@hugerte/hugerte-react';

import type { Props as RichMediaTextEditorProps } from './RichMediaTextEditor';
import { RichMediaTextEditorWrapper } from './RichMediaTextEditorWrapper';

type EditorInitConfig = EditorProps['init'];

/**
 * The published petition description is styled by `CONTENT_CLASSES` in
 * `#src/app/shared/components/html/Html.tsx`. These list rules must stay equivalent to the
 * list rules there. Nothing enforces that, and the editor is only WYSIWYG while the two agree.
 *
 * They live here rather than in the shared editor config because only the petition
 * description is published through `Html.tsx`. Admin comms content, for example, is rendered
 * by an email template, so petition spacing there would describe a layout the reader never
 * sees.
 *
 * The lists need an explicit `padding: 0` because the editor iframe gets no Tailwind preflight
 * reset, so the browser default list padding would otherwise indent them past the page.
 */
const PETITION_DESCRIPTION_CONTENT_STYLE = `
	ol,
	ul {
		margin: 1.5em 3em;
		padding: 0;
	}
	ol {
		list-style-type: decimal;
	}
	ul {
		list-style-type: circle;
	}
	ol > li,
	ul > li {
		margin: 0.75em 0.5em;
	}
`;

/**
 * The petition description editor: `RichMediaTextEditor` plus the styling that makes it match
 * the published description.
 *
 * `overridesEditorConfig` is supported, and its `content_style` is appended after these rules
 * so a caller can specialize them. Two caveats for callers:
 *
 * - Only `content_style` is concatenated down the chain. Every other key replaces the value
 *   below it, so passing `plugins` or `toolbar` drops the media buttons.
 * - An `@import` in an override is silently ignored, because CSS only allows `@import` as the
 *   first rule of a stylesheet and the shared config has already emitted one. Inline
 *   `@font-face` rules instead.
 */
export const PetitionDescriptionEditor: React.FC<RichMediaTextEditorProps> = ({ overridesEditorConfig, ...props }) => {
	const mergedOverrides: EditorInitConfig = {
		...overridesEditorConfig,
		content_style: `${PETITION_DESCRIPTION_CONTENT_STYLE}\n${overridesEditorConfig?.content_style || ''}`,
	};

	return <RichMediaTextEditorWrapper {...props} overridesEditorConfig={mergedOverrides} />;
};
